import { socialExport } from '@mi-front/api-clients';
import { AutocompleteElement, useFormContext, useWatch } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { YoutubePlaylistListItem } from './YoutubePlaylistListItem';
import { YoutubePlaylistTag } from './YoutubePlaylistTag';

type Props = {
  playlists: socialExport.YoutubePlaylistDto[];
  isLoading: boolean;
  maxSelected: number;
  helperText?: React.ReactNode;
};

/**
 * Autocomplete component for selecting YouTube playlists.
 * Handles the autocomplete UI logic including option rendering, tags, and disabled states.
 * Manages the max selected limit internally by watching the form value.
 */
export function YoutubePlaylistsAutocomplete({
  playlists,
  isLoading,
  maxSelected,
  helperText,
}: Props) {
  const { control } = useFormContext();
  const { $t } = useIntl();
  const selectedPlaylists = useWatch({ control, name: 'playlistsIds' }) as
    | socialExport.YoutubePlaylistDto[]
    | null;
  const selectedCount = selectedPlaylists?.length ?? 0;
  const isMaxReached = selectedCount >= maxSelected;

  return (
    <AutocompleteElement
      name="playlistsIds"
      control={control}
      options={playlists}
      multiple
      loading={isLoading}
      label={$t({
        id: 'socialExport.youtube.playlist.label',
        defaultMessage: 'Playlists',
      })}
      textFieldProps={{
        fullWidth: true,
        helperText: helperText,
      }}
      autocompleteProps={{
        fullWidth: true,
        getOptionLabel: (option: socialExport.YoutubePlaylistDto) => {
          if (typeof option === 'string') return option;
          return option.title;
        },
        isOptionEqualToValue: (
          option: socialExport.YoutubePlaylistDto,
          value: socialExport.YoutubePlaylistDto,
        ) => {
          return option.id === value?.id;
        },
        getOptionDisabled: (option: socialExport.YoutubePlaylistDto) => {
          // Disable option if max limit reached and this option is not already selected
          if (isMaxReached) {
            const isSelected = selectedPlaylists?.some((p) => p.id === option.id);
            return !isSelected;
          }
          return false;
        },
        renderOption: (props, option: socialExport.YoutubePlaylistDto) => (
          <YoutubePlaylistListItem {...props} playlist={option} key={option.id} />
        ),
        renderTags: (value: socialExport.YoutubePlaylistDto[], getTagProps) =>
          value.map((option: socialExport.YoutubePlaylistDto, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <YoutubePlaylistTag key={key} playlist={option} {...tagProps} />;
          }),
        noOptionsText: $t({
          id: 'socialExport.youtube.playlist.noOptions',
          defaultMessage: 'No hay playlists disponibles',
        }),
      }}
    />
  );
}
